import { useState, useEffect } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from 'react-leaflet'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import axios from 'axios'
import Container from './styles'

// Fix Leaflet default marker icons
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png'
import markerIcon from 'leaflet/dist/images/marker-icon.png'
import markerShadow from 'leaflet/dist/images/marker-shadow.png'

delete L.Icon.Default.prototype._getIconUrl
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
})

// Component to fit map bounds
const FitBounds = ({ bounds }) => {
  const map = useMap()
  
  useEffect(() => {
    if (bounds && bounds.length > 0) {
      map.fitBounds(bounds, { padding: [50, 50] })
    }
  }, [bounds, map])
  
  return null
}

const EmbedTrackOrder = () => {
  const { trackingNumber } = useParams()
  const [searchParams] = useSearchParams()
  const [orderData, setOrderData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [routeCoordinates, setRouteCoordinates] = useState([])

  // Customization from URL params
  const config = {
    primaryColor: searchParams.get('primaryColor') || '#FE7D11',
    pickupColor: searchParams.get('pickupColor') || '#4CAF50',
    deliveryColor: searchParams.get('deliveryColor') || '#FE7D11',
    riderColor: searchParams.get('riderColor') || '#2196F3',
    showLegend: searchParams.get('showLegend') !== 'false',
    showRoute: searchParams.get('showRoute') !== 'false',
    autoRefresh: searchParams.get('autoRefresh') !== 'false',
    refreshInterval: parseInt(searchParams.get('refreshInterval') || '15000'),
    mapStyle: searchParams.get('mapStyle') || 'default', // default, dark, satellite
  }

  // Custom marker icons with dynamic colors
  const createIcon = (emoji, color) => new L.DivIcon({
    html: `<div style="background: ${color}; width: 32px; height: 32px; border-radius: 50%; display: flex; align-items: center; justify-content: center; border: 3px solid white; box-shadow: 0 2px 8px rgba(0,0,0,0.3);">
      <span style="color: white; font-size: 18px;">${emoji}</span>
    </div>`,
    className: '',
    iconSize: [32, 32],
    iconAnchor: [16, 16],
  })

  const createRiderIcon = (color) => new L.DivIcon({
    html: `<div style="background: ${color}; width: 40px; height: 40px; border-radius: 50%; display: flex; align-items: center; justify-content: center; border: 3px solid white; box-shadow: 0 4px 12px rgba(${hexToRgb(color)}, 0.5); animation: pulse 2s infinite;">
      <span style="color: white; font-size: 20px;">🏍️</span>
    </div>`,
    className: '',
    iconSize: [40, 40],
    iconAnchor: [20, 20],
  })

  const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
    return result 
      ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}`
      : '33, 150, 243'
  }

  const getTileLayerUrl = () => {
    switch (config.mapStyle) {
      case 'dark':
        return 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png'
      case 'satellite':
        return 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'
      default:
        return 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
    }
  }

  const getTileAttribution = () => {
    switch (config.mapStyle) {
      case 'dark':
        return '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
      case 'satellite':
        return '&copy; <a href="https://www.esri.com/">Esri</a>'
      default:
        return '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
    }
  }

  useEffect(() => {
    if (trackingNumber) {
      fetchOrderTracking(trackingNumber)
    } else {
      setLoading(false)
      setError('No tracking number provided')
    }
  }, [trackingNumber])

  // Auto-refresh rider location
  useEffect(() => {
    if (!orderData || !trackingNumber || !config.autoRefresh) return

    const refreshInterval = setInterval(() => {
      fetchOrderTracking(trackingNumber, true)
    }, config.refreshInterval)

    return () => clearInterval(refreshInterval)
  }, [orderData, trackingNumber, config.autoRefresh, config.refreshInterval])

  const fetchOrderTracking = async (trackingNum, silent = false) => {
    if (!silent) {
      setLoading(true)
      setError(null)
    }
    
    try {
      const response = await axios.post(
        `https://api.astar.ng/api.php?path=/api/v1/admin/order_details`,
        { trackingNumber: trackingNum }
      )

      if (response.data.status === 200 && response.data.data) {
        const order = response.data.data.order || response.data.data
        setOrderData(order)
        if (!silent) setLoading(false)
      } else {
        if (!silent) {
          setError(response.data.message || 'Order not found')
          setLoading(false)
        }
      }
    } catch (err) {
      if (!silent) {
        setError(err.response?.data?.message || 'Failed to fetch order tracking')
        setLoading(false)
      }
    }
  }

  const getMapBounds = () => {
    if (!orderData) return []

    const bounds = [
      [orderData.pickupLat, orderData.pickupLng],
      [orderData.deliveryLat, orderData.deliveryLng],
    ]
    
    if (orderData.riderLat && orderData.riderLng) {
      bounds.push([orderData.riderLat, orderData.riderLng])
    }

    return bounds
  }

  const decodePolyline = (encoded) => {
    const points = []
    let index = 0
    const len = encoded.length
    let lat = 0
    let lng = 0

    while (index < len) {
      let b
      let shift = 0
      let result = 0
      do {
        b = encoded.charCodeAt(index++) - 63
        result |= (b & 0x1f) << shift
        shift += 5
      } while (b >= 0x20)
      const dlat = (result & 1) !== 0 ? ~(result >> 1) : result >> 1
      lat += dlat

      shift = 0
      result = 0
      do {
        b = encoded.charCodeAt(index++) - 63
        result |= (b & 0x1f) << shift
        shift += 5
      } while (b >= 0x20)
      const dlng = (result & 1) !== 0 ? ~(result >> 1) : result >> 1
      lng += dlng

      points.push([lat / 1e5, lng / 1e5])
    }

    return points
  }

  const fetchRoute = async (start, end) => {
    try {
      const response = await axios.get(
        `https://router.project-osrm.org/route/v1/driving/${start[1]},${start[0]};${end[1]},${end[0]}?overview=full&geometries=polyline`
      )

      if (response.data.code === 'Ok' && response.data.routes && response.data.routes.length > 0) {
        const route = response.data.routes[0]
        return decodePolyline(route.geometry)
      }
    } catch (error) {
      console.error('Error fetching route:', error)
    }
    
    return [start, end]
  }

  const updateRouteCoordinates = async () => {
    if (!orderData || !config.showRoute) return

    try {
      const pickup = [orderData.pickupLat, orderData.pickupLng]
      const delivery = [orderData.deliveryLat, orderData.deliveryLng]
      const rider = orderData.riderLat && orderData.riderLng 
        ? [orderData.riderLat, orderData.riderLng] 
        : null

      const routeData = rider 
        ? await fetchRoute(rider, delivery)
        : await fetchRoute(pickup, delivery)

      setRouteCoordinates(routeData)
    } catch (error) {
      console.error('Error updating route:', error)
      const pickup = [orderData.pickupLat, orderData.pickupLng]
      const delivery = [orderData.deliveryLat, orderData.deliveryLng]
      setRouteCoordinates([pickup, delivery])
    }
  }

  useEffect(() => {
    if (orderData) {
      updateRouteCoordinates()
    }
  }, [orderData, config.showRoute])

  if (loading) {
    return (
      <Container $primaryColor={config.primaryColor}>
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading...</p>
        </div>
      </Container>
    )
  }

  if (error || !orderData) {
    return (
      <Container $primaryColor={config.primaryColor}>
        <div className="error-container">
          <div className="error-icon">📍</div>
          <p>{error || 'No tracking data available'}</p>
        </div>
      </Container>
    )
  }

  return (
    <Container 
      $primaryColor={config.primaryColor}
      $pickupColor={config.pickupColor}
      $deliveryColor={config.deliveryColor}
      $riderColor={config.riderColor}
    >
      <div className="map-wrapper">
        <MapContainer
          center={[orderData.pickupLat, orderData.pickupLng]}
          zoom={13}
          style={{ width: '100%', height: '100%' }}
          zoomControl={true}
          scrollWheelZoom={true}
        >
          <TileLayer
            attribution={getTileAttribution()}
            url={getTileLayerUrl()}
          />
          
          {/* Pickup Marker */}
          <Marker 
            position={[orderData.pickupLat, orderData.pickupLng]}
            icon={createIcon('📦', config.pickupColor)}
          >
            <Popup>
              <div style={{ padding: '8px' }}>
                <strong>Pickup Location</strong>
                <p style={{ margin: '8px 0 0 0', fontSize: '12px' }}>
                  {orderData.pickupAddress}
                </p>
              </div>
            </Popup>
          </Marker>

          {/* Delivery Marker */}
          <Marker 
            position={[orderData.deliveryLat, orderData.deliveryLng]}
            icon={createIcon('🎯', config.deliveryColor)}
          >
            <Popup>
              <div style={{ padding: '8px' }}>
                <strong>Delivery Location</strong>
                <p style={{ margin: '8px 0 0 0', fontSize: '12px' }}>
                  {orderData.deliveryAddress}
                </p>
                {orderData.customerName && (
                  <p style={{ margin: '4px 0 0 0', fontSize: '12px' }}>
                    <strong>Customer:</strong> {orderData.customerName}
                  </p>
                )}
              </div>
            </Popup>
          </Marker>

          {/* Rider Marker */}
          {orderData.riderLat && orderData.riderLng && (
            <Marker 
              position={[orderData.riderLat, orderData.riderLng]}
              icon={createRiderIcon(config.riderColor)}
            >
              <Popup>
                <div style={{ padding: '8px' }}>
                  <strong>Rider Location</strong>
                  <p style={{ margin: '8px 0 0 0', fontSize: '12px' }}>
                    <strong>{orderData.riderName || 'Rider'}</strong>
                  </p>
                  {orderData.riderPhone && (
                    <p style={{ margin: '4px 0 0 0', fontSize: '12px' }}>
                      📞 {orderData.riderPhone}
                    </p>
                  )}
                  <div style={{ 
                    marginTop: '8px', 
                    padding: '4px 8px', 
                    background: '#4CAF50', 
                    color: 'white', 
                    borderRadius: '4px', 
                    fontSize: '11px',
                    fontWeight: 'bold',
                    display: 'inline-block'
                  }}>
                    LIVE
                  </div>
                </div>
              </Popup>
            </Marker>
          )}

          {/* Route Polyline */}
          {config.showRoute && routeCoordinates.length > 0 && (
            <Polyline 
              positions={routeCoordinates}
              color={config.primaryColor}
              weight={5}
              opacity={0.8}
            />
          )}

          <FitBounds bounds={getMapBounds()} />
        </MapContainer>

        {/* Map Legend */}
        {config.showLegend && (
          <div className="map-legend">
            <div className="legend-item">
              <span className="legend-marker" style={{ background: config.pickupColor }}></span>
              <span>Pickup</span>
            </div>
            <div className="legend-item">
              <span className="legend-marker" style={{ background: config.deliveryColor }}></span>
              <span>Delivery</span>
            </div>
            {orderData.riderLat && orderData.riderLng && (
              <div className="legend-item">
                <span className="legend-marker" style={{ background: config.riderColor }}></span>
                <span>Rider (Live)</span>
              </div>
            )}
          </div>
        )}
      </div>
    </Container>
  )
}

export default EmbedTrackOrder
